/**
 * NCR Create Page - Create a new Non-Conformance Report
 * Based on Portal v1.89 NCR implementation
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { qualityAPI, suppliersAPI, coreAPI, platformSettingsAPI, fetchAllPages } from '../lib/api';
import type { SourceOfRejection, FaultCodeGroup, FaultCodeCategory, UserProfile, QualityTabsVisibility, Warehouse, SupplierProfile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useFormatters } from '../contexts/PreferencesContext';
import { toast } from 'sonner';
import NCRDetailsForm, { type NCRFormData, type RepeatInfo } from '../components/quality/ncr-create/NCRDetailsForm';
import NCRGrnLinkDialog from '../components/quality/ncr-create/NCRGrnLinkDialog';
import { extractNcrError } from './ncr/ncrCreateErrors';
import { useGrnLookup } from './ncr/useGrnLookup';
import { useBuyerAutofill } from './ncr/useBuyerAutofill';
import { DEFECT_DESCRIPTION_MAX_LENGTH, isDefectDescriptionTooLong } from '../components/quality/defectDescriptionLimit';

export default function NCRCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Admin toggle: when on, Fault Group/Category are optional on this form.
  const { ncrFaultClassificationOptional } = useMenuSettings();
  // CR-4707 precision, same source the PO Line pop-up reads.
  const { preferences } = useFormatters();
  const priceDecimals = preferences.unit_price_decimals;
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [grnLinked, setGrnLinked] = useState(false);
  const [customerUsers, setCustomerUsers] = useState<UserProfile[]>([]);
  // CR-7347: warehouses this user may raise an NCR against. Same source as the
  // register's warehouse filter, so the picker and the filter agree.
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if NCRs tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if NCRs tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.ncrs) {
      navigate('/quality', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.ncrs, navigate]);

  // Classification dropdown options
  const [sourcesOfRejection, setSourcesOfRejection] = useState<SourceOfRejection[]>([]);
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [faultCategories, setFaultCategories] = useState<FaultCodeCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<FaultCodeCategory[]>([]);
  // CR-6387: per-field errors returned by the backend, surfaced inline.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // GRN dialog state
  const [showGrnDialog, setShowGrnDialog] = useState(true);
  const [grnInput, setGrnInput] = useState('');

  const [formData, setFormData] = useState<NCRFormData>({
    supplier: null,
    customer_reference: '',
    proposed_rejection_type: 0,
    part_number: '',
    description: '',
    batch_number: '',
    batch_qty: '',
    reject_qty: '',
    shortage_qty: '',
    defect_description: '',
    grn_number: '',
    po_number: '',
    unit_price: '',
    works_order: '',
    po_line: null,
    po_line_number: '',
    grn: null,
    warehouse: null,
    source_of_rejection_option: null,
    fault_group: null,
    fault_category: null,
    liability_type: 0,
    is_collection_required: null,
    is_credit_required: null,
    is_replacement_required: null,
    is_internal: false,
    assigned_to: null,
    buyer_name: '',
    is_8d_required: null,
    containment_required: false,
    containment_qty_ok: '',
    containment_qty_not_ok: '',
    containment_current_stock: '',
  });
  const { doGRNLookup, lookingUpGRN } = useGrnLookup(setFormData, setGrnLinked, priceDecimals);

  // Customer review 2026-07: live repeat-issue flag (same part + fault group
  // raised before) and the ERP current-stock snapshot for containment.
  const [repeatInfo, setRepeatInfo] = useState<RepeatInfo | null>(null);
  const [currentStock, setCurrentStock] = useState<number | null>(null);

  useEffect(() => {
    if (!formData.part_number.trim() || !formData.fault_group) {
      setRepeatInfo(null);
      return;
    }
    const t = setTimeout(() => {
      qualityAPI.ncrRepeatCheck({ part_number: formData.part_number.trim(), fault_group: formData.fault_group as number })
        .then(res => setRepeatInfo(res.data))
        .catch(() => setRepeatInfo(null));
    }, 400);
    return () => clearTimeout(t);
  }, [formData.part_number, formData.fault_group]);

  useEffect(() => {
    if (!formData.containment_required || !formData.part_number.trim()) {
      setCurrentStock(null);
      return;
    }
    const t = setTimeout(() => {
      qualityAPI.ncrPartStock({ part_number: formData.part_number.trim() })
        .then(res => setCurrentStock(res.data.stock_quantity))
        .catch(() => setCurrentStock(null));
    }, 400);
    return () => clearTimeout(t);
  }, [formData.containment_required, formData.part_number]);

  // Fetch suppliers and classification options
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        // Walk pages — the backend caps page_size at 1000, so a customer with
        // more suppliers than that could not raise an NCR against the rest.
        const profiles = await fetchAllPages<SupplierProfile>((page) => suppliersAPI.getProfiles({ page_size: 1000, page, minimal: true }));
        const mapped = profiles.map(p => ({ id: p.company_id, name: p.company_name }));
        const unique = Array.from(new Map(mapped.map(s => [s.id, s])).values())
          .sort((a, b) => a.name.localeCompare(b.name));
        setSuppliers(unique);
      } catch (error: unknown) {
        console.error('Error fetching suppliers:', error);
        toast.error('Failed to load suppliers');
      } finally {
        setLoadingSuppliers(false);
      }
    };

    const fetchClassificationOptions = async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const [sors, fgs, fcs] = await Promise.all([
          fetchAllPages<SourceOfRejection>((page) => qualityAPI.getSourcesOfRejection({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeGroup>((page) => qualityAPI.getFaultGroups({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeCategory>((page) => qualityAPI.getFaultCategories({ is_active: true, page_size: 100, page })),
        ]);
        setSourcesOfRejection(sors);
        setFaultGroups(fgs);
        setFaultCategories(fcs);
      } catch (error: unknown) {
        console.error('Error fetching classification options:', error);
      }
    };

    const fetchCustomerUsers = async () => {
      if (user?.company) {
        try {
          const response = await coreAPI.getCompanyUsers(user.company);
          setCustomerUsers(response.data);
        } catch {
          console.error('Error fetching customer users');
        }
      }
    };

    // CR-7347: the Warehouse picker's options. Same endpoint the register's
    // warehouse filter uses, so a warehouse that can be filtered on can also
    // be raised against. A failure leaves the picker empty and disabled
    // rather than blocking the whole form.
    const fetchWarehouses = async () => {
      try {
        const response = await coreAPI.getAccessibleWarehouses();
        setWarehouses(Array.isArray(response.data) ? response.data : []);
      } catch {
        console.error('Error fetching warehouses');
        setWarehouses([]);
      }
    };

    fetchSuppliers();
    fetchClassificationOptions();
    fetchCustomerUsers();
    fetchWarehouses();
  }, [user?.company]);

  const updateField = (field: keyof NCRFormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // CR-6387: clear any backend error for this field once the user edits it.
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    // QUALITY-NCR-REVIEW #5 (I3/I4): "Handle internally" belongs to customer
    // liability, and an internal NCR never goes to the supplier, so it has no
    // collection and no supplier stock containment.
    if (field === 'liability_type' && value !== 2) {
      setFormData(prev => ({ ...prev, is_internal: false, assigned_to: null }));
    }
    // E26: a customer-liability NCR doesn't ask the supplier to contain stock.
    // Round 4: nor for a credit note or replacement -- the form hides those
    // boxes for customer liability, so a value chosen earlier must not ride along.
    if (field === 'liability_type' && value === 2) {
      setFormData(prev => ({ ...prev, containment_required: false, is_credit_required: false, is_replacement_required: false }));
    }
    if (field === 'is_internal' && value === true) {
      setFormData(prev => ({ ...prev, is_collection_required: false, containment_required: false }));
    }
    if (field === 'grn_number') {
      setGrnLinked(false);
      setFormData(prev => ({ ...prev, [field]: value as string, po_line: null, po_line_number: '', grn: null }));
    }
    // When fault group changes, filter categories and reset category selection
    if (field === 'fault_group') {
      const groupId = value as number | null;
      if (groupId) {
        setFilteredCategories(faultCategories.filter(c => c.fault_group === groupId));
      } else {
        setFilteredCategories([]);
      }
      setFormData(prev => ({ ...prev, fault_group: groupId, fault_category: null }));
    }
  };

  // CR-7138: Buyer follows the PO line the NCR is raised against.
  useBuyerAutofill(formData, updateField, customerUsers);

  const handleDialogLookup = async () => {
    const trimmed = grnInput.trim();
    if (!trimmed) {
      toast.error('Please enter a GRN number');
      return;
    }
    const success = await doGRNLookup(trimmed, suppliers);
    if (success) {
      setShowGrnDialog(false);
    }
  };

  const handleSkipGrn = () => {
    setShowGrnDialog(false);
  };

  // B5 (spec single SUBMIT): "Raise NCR" creates and issues it in one step;
  // "Save as draft" keeps the two-step path.
  const handleSubmit = async (raise = false) => {
    // CR-6387: collect ALL client-side validation errors up front so each one
    // surfaces inline (red border + message), not just a toast — and so the
    // stale-error clear happens only once every check passes. Clearing at the
    // very top and then returning early left the user with a toast but no
    // inline indication of the offending field.
    const clientErrors: Record<string, string> = {};
    if (!formData.supplier) clientErrors['supplier'] = 'Please select a supplier.';
    // QUALITY-NCR-REVIEW #4 (I2): required only once the company has set up
    // its sources (Admin Setup > Quality). An empty list used to block Create.
    if (sourcesOfRejection.length > 0 && !formData.source_of_rejection_option) clientErrors['source_of_rejection_option'] = 'Please select a source of rejection.';
    // Fault Group/Category are mandatory unless the admin has made them optional.
    if (!ncrFaultClassificationOptional) {
      if (!formData.fault_group) clientErrors['fault_group'] = 'Please select a fault group.';
      if (!formData.fault_category) clientErrors['fault_category'] = 'Please select a fault category.';
    }
    if (!formData.defect_description.trim()) clientErrors['defect_description'] = 'Please enter a defect description.';
    else if (isDefectDescriptionTooLong(formData.defect_description)) clientErrors['defect_description'] = `Please shorten the description to ${DEFECT_DESCRIPTION_MAX_LENGTH} characters or fewer.`;
    // Liability is required (marked * in the UI); 0 means no owner picked.
    if (!formData.liability_type) clientErrors['liability_type'] = 'Please select a liability.';
    if (formData.containment_required) {
      if (formData.containment_qty_ok === '') clientErrors['containment_qty_ok'] = 'Required when requesting stock containment.';
      if (formData.containment_qty_not_ok === '') clientErrors['containment_qty_not_ok'] = 'Required when requesting stock containment.';
    }
    // The required Yes/No actions are tri-state — an unselected (null) field must
    // NOT silently submit as "No". Require an explicit choice for the fields
    // actually shown for this liability owner.
    if (formData.liability_type > 0) {
      const requiredYesNo: Array<keyof NCRFormData> = ['is_8d_required', 'is_collection_required'];
      if (formData.liability_type === 1) {
        requiredYesNo.push('is_credit_required', 'is_replacement_required');
      }
      for (const key of requiredYesNo) {
        if (formData[key] === null) clientErrors[key] = 'Please choose Yes or No.';
      }
    }

    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      toast.error('Please correct the highlighted fields before submitting.');
      return;
    }

    // All client-side checks passed — clear any stale (backend) errors and submit.
    setFieldErrors({});

    try {
      setSaving(true);

      const apiData: Record<string, unknown> = {
        supplier: formData.supplier,
        defect_description: formData.defect_description,
      };

      if (formData.customer_reference) apiData['customer_reference'] = formData.customer_reference;
      if (formData.part_number) apiData['part_number'] = formData.part_number;
      if (formData.description) apiData['description'] = formData.description;
      if (formData.batch_number) apiData['batch_number'] = formData.batch_number;
      if (formData.grn_number) apiData['grn_number'] = formData.grn_number;
      if (formData.po_number) apiData['po_number'] = formData.po_number;
      if (formData.works_order) apiData['works_order'] = formData.works_order;
      if (formData.unit_price) apiData['unit_price'] = formData.unit_price;
      if (formData.po_line) apiData['po_line'] = formData.po_line;
      if (formData.grn) apiData['grn'] = formData.grn;
      // CR-7347: only send it when picked. Omitted, the server inherits the
      // linked order's warehouse; sending null would override that with
      // "no warehouse".
      if (formData.warehouse) apiData['warehouse'] = formData.warehouse;
      apiData['source_of_rejection_option'] = formData.source_of_rejection_option;
      apiData['fault_group'] = formData.fault_group;
      apiData['fault_category'] = formData.fault_category;
      apiData['liability_type'] = formData.liability_type;
      // Tri-state selects: null (unselected) submits as false — the backend
      // BooleanFields don't accept null.
      apiData['is_8d_required'] = formData.is_8d_required ?? false;
      apiData['is_collection_required'] = formData.is_collection_required ?? false;
      apiData['is_credit_required'] = formData.is_credit_required ?? false;
      apiData['is_replacement_required'] = formData.is_replacement_required ?? false;
      apiData['is_internal'] = formData.is_internal;
      apiData['assigned_to'] = formData.assigned_to;
      if (formData.buyer_name) apiData['buyer_name'] = formData.buyer_name;

      // Left blank means nothing to report, which the model stores as 0.
      apiData['batch_qty'] = Number(formData.batch_qty) || 0;
      apiData['reject_qty'] = Number(formData.reject_qty) || 0;
      apiData['shortage_qty'] = Number(formData.shortage_qty) || 0;

      // Containment status (customer review 2026-07)
      apiData['containment_required'] = formData.containment_required;
      if (formData.containment_required) {
        apiData['containment_qty_ok'] = formData.containment_qty_ok;
        apiData['containment_qty_not_ok'] = formData.containment_qty_not_ok;
        // Prefer the ERP snapshot; fall back to the manually-entered figure
        // for tenants with no stock feed.
        if (currentStock !== null) {
          apiData['containment_current_stock'] = currentStock;
        } else if (formData.containment_current_stock !== '') {
          apiData['containment_current_stock'] = Number(formData.containment_current_stock);
        }
      }

      if (formData.proposed_rejection_type) apiData['proposed_rejection_type'] = formData.proposed_rejection_type;
      const response = await qualityAPI.createNCR(apiData);
      if (raise) {
        try {
          await qualityAPI.submitDetail(response.data.id);
          toast.success('NCR raised');
        } catch (submitError: unknown) {
          // Created but not issued: say why, and open the draft to finish it.
          toast.error(`Saved as a draft: ${extractNcrError(submitError).message}`);
        }
      } else {
        toast.success('Draft saved');
      }
      navigate(`/quality/ncr/${response.data.id}/edit`);
    } catch (error: unknown) {
      console.error('Error creating NCR:', error);
      // CR-6387: surface the real reason (and highlight offending fields)
      // instead of a blanket "Failed to create NCR".
      const { message, fields } = extractNcrError(error);
      setFieldErrors(fields);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Show loading while checking tab settings
  if (!tabSettingsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  const handleContainmentChange = (
    field: 'containment_required' | 'containment_qty_ok' | 'containment_qty_not_ok' | 'containment_current_stock',
    value: boolean | string,
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // CR-6387: clear a containment field's backend error as it's edited.
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <NCRGrnLinkDialog
        open={showGrnDialog}
        onOpenChange={setShowGrnDialog}
        value={grnInput}
        onValueChange={setGrnInput}
        lookingUp={lookingUpGRN}
        onLookup={handleDialogLookup}
        onSkip={handleSkipGrn}
      />

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/quality')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create New NCR</h1>
          <p className="text-muted-foreground">Create a new Non-Conformance Report</p>
        </div>
      </div>

      <NCRDetailsForm
        formData={formData}
        fieldErrors={fieldErrors}
        updateField={updateField}
        onContainmentChange={handleContainmentChange}
        suppliers={suppliers}
        loadingSuppliers={loadingSuppliers}
        grnLinked={grnLinked}
        lookingUpGRN={lookingUpGRN}
        doGRNLookup={doGRNLookup}
        sourcesOfRejection={sourcesOfRejection}
        faultGroups={faultGroups}
        filteredCategories={filteredCategories}
        customerUsers={customerUsers}
        warehouses={warehouses}
        repeatInfo={repeatInfo}
        currentStock={currentStock}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/quality')}
      />
    </div>
  );
}
